import { Component, Host, h, Prop, Watch, Element, State, Listen } from '@stencil/core';
import { ActionTrackingEvent } from '@union-investment/tracking/src/events';
import { DEFAULT_TRACKING_DELAY } from '../../global/utility/tracking-helpers';
import { CmsLink } from '../../types/global';
import { replaceCheckmarkIcons } from '../../global/utility/utils';

export interface StepItem {
  headline: string;
  iconPath?: string;
  text: string;
  link?: CmsLink;
}

export interface UiCmsProgressBarBody {
  steps: StepItem[];
  activeStep: number;
  uniqueId: string;
  isAlignLinks: boolean;
}

@Component({
  tag: 'ui-cms-progress-bar',
  styleUrl: 'ui-cms-progress-bar.scss',
  shadow: false,
})
export class UiCmsProgressBar {
  @Element() el: HTMLElement;

  @Prop({ mutable: true }) body: UiCmsProgressBarBody = {} as any;

  @Prop({ attribute: 'body' }) utilityBody: string;

  @State() size = 'small' as 'small' | 'medium' | 'large';

  @State() progressStepWidth = 0;

  @State() isMobile = false;

  @Watch('utilityBody')
  bodyChangeHandler(newValue: string) {
    this.body = newValue ? JSON.parse(newValue) : this.body;
  }

  @Listen('resize', { target: 'window' })
  onWindowResize() {
    this.isMobile = window.innerWidth < 768;
  }

  componentWillLoad() {
    this.bodyChangeHandler(this.utilityBody);
  }

  componentDidLoad() {
    this.setupResizeObserverToSetViewMode();
    replaceCheckmarkIcons(this.el);
  }

  private setupResizeObserverToSetViewMode() {
    const resizeObserver = new ResizeObserver(() => {
      const width = this.el.clientWidth;
      this.progressStepWidth = this.el.querySelector('.progress-step')?.clientWidth;
      if (width < 740) {
        this.el.style.setProperty('--dynamic-width', `${width}px`);
        this.size = 'small';
      } else if (width < 1024) {
        this.size = 'medium';
      } else {
        this.size = 'large';
      }
    });
    resizeObserver.observe(this.el);
  }

  onLinkClicked(index: number) {
    const stepItem: StepItem = this.body.steps[index];
    const isExternalLink = stepItem.link.src.includes('https:');
    const attributesTargetKey = isExternalLink ? 'externalTarget' : 'target';

    const trackingEvent: ActionTrackingEvent = {
      eventInfo: {
        eventName: `Click ${isExternalLink ? 'External' : 'Internal'} Link in Progress Bar`,
        eventType: 'Action',
        eventCaller: this.el,
      },
      attributes: {
        elementType: 'Link',
        elementLabel: stepItem.link.title,
        [attributesTargetKey]: isExternalLink ? stepItem.link.src : stepItem.link.contentId,
      },
      category: {
        area: 'Content',
      },
    };

    if (trackingEvent) {
      setTimeout(() => {
        window.ui?.services?.tracking?.track(trackingEvent);
      }, DEFAULT_TRACKING_DELAY);
    }
  }

  private renderIcon = (icon: string, color: string) => (
    <sd-icon library={window.ui.cmsState.iconLibrary} color={color} name={icon}></sd-icon>
  );

  private renderLink = (link: CmsLink, index: number) => (
    <div
      class={{
        'progress-step__button': true,
        'progress-step__button--plain': !link.buttonVariant,
        'progress-step__button__alignLink': this.body.isAlignLinks,
      }}
    >
      {link.buttonVariant ? (
        <sd-button
          href={link.src}
          onClick={() => this.onLinkClicked.bind(this, index)}
          target={link.target}
          variant={link.buttonVariant}
          title={link.title}
        >
          {link.iconPath && (
            <sd-icon library={window.ui.cmsState.iconLibrary} slot="icon-left" name={link.iconPath}></sd-icon>
          )}
          {link.title}
        </sd-button>
      ) : (
        <sd-link onClick={this.onLinkClicked.bind(this, index)} size="lg" href={link.src} target={link.target}>
          {link.iconPath && (
            <sd-icon library={window.ui.cmsState.iconLibrary} slot="icon-left" name={link.iconPath}></sd-icon>
          )}
          <span aria-hidden={!!link.ariaLabel}>{link.title}</span>
          <span class="sr-only">{link.ariaLabel}</span>
        </sd-link>
      )}
    </div>
  );

  private renderProgressStep = (step: StepItem, index: number) => (
    <div
      class={{
        'progress-step': true,
        'progress-step--active': index < this.body.activeStep,
        [`progress-step--${this.size}`]: true,
      }}
    >
      <div
        class={{
          'progress-step__symbol': true,
          'progress-step__symbol--icon': !!step.iconPath,
          'progress-step__symbol--count': !step.iconPath,
        }}
      >
        {step.iconPath ? this.renderIcon(step.iconPath, 'primary') : index + 1}
        <div class="progress-step__activeIcon">{this.renderIcon('system/confirm', 'white')}</div>
      </div>
      <h4 class="sd-headline sd-headline--size-base progress-step__headline">{step.headline}</h4>
      <div class="sd-prose progress-step__text" innerHTML={step.text}></div>
      {step.link && this.renderLink(step.link, index)}
    </div>
  );

  render() {
    return (
      <Host>
        {this.isMobile ? (
          <sd-carousel>
            {this.body.steps.map((step, index) => (
              <sd-carousel-item>{this.renderProgressStep(step, index)}</sd-carousel-item>
            ))}
          </sd-carousel>
        ) : (
          <sd-scrollable buttons="true" step={this.progressStepWidth}>
            {this.body.steps.map((step, index) => this.renderProgressStep(step, index))}
          </sd-scrollable>
        )}
      </Host>
    );
  }
}
